import React, { useState, useEffect } from 'react';
import { mediaAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';
import Pagination from '../../components/common/Pagination';

const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [viewModal, setViewModal] = useState({ show: false, item: null });
  const [confirmModal, setConfirmModal] = useState({ show: false, mediaId: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchMedia();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [currentPage, type, appliedSearch]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await mediaAPI.getAll({ limit: 24, page: currentPage, type, search: appliedSearch });
      if (response.data.success) {
        setMedia(response.data.data.media || []);
        setTotalPages(response.data.data.pagination?.total_pages || 1);
      }
    } catch (error) {
      setToast({ show: true, message: 'Failed to fetch media', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
    setCurrentPage(1);
  };

  const fetchStats = async () => {
    try {
      const response = await mediaAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await mediaAPI.upload(formData);
      if (response.data.success) {
        setToast({ show: true, message: 'File uploaded successfully', type: 'success' });
        fetchMedia();
        fetchStats();
      }
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.message || 'Upload failed', type: 'error' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    try {
      await mediaAPI.delete(id);
      setToast({ show: true, message: 'Media deleted successfully', type: 'success' });
      fetchMedia();
      fetchStats();
      setConfirmModal({ show: false, mediaId: null });
    } catch (error) {
      setToast({
        show: true,
        message: error.response?.data?.message || 'Failed to delete media',
        type: 'error',
      });
    }
  };

  // Format bytes to MB/KB
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      
      <ConfirmModal
        show={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, mediaId: null })}
        onConfirm={() => handleDelete(confirmModal.mediaId)}
        title="Delete Media"
        message="Are you sure you want to delete this file? This action cannot be undone."
        type="danger"
      />

      {/* VIEW MODAL */}
      {viewModal.show && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{viewModal.item?.original_filename}</h5>
                <button type="button" className="btn-close" onClick={() => setViewModal({ show: false, item: null })}></button>
              </div>
              <div className="modal-body text-center">
                {viewModal.item?.file_type === 'image' ? (
                  <img src={viewModal.item.url} alt={viewModal.item.original_filename} className="img-fluid" />
                ) : (
                  <video src={viewModal.item.url} controls className="w-100" />
                )}
                <div className="mt-3 text-start">
                  <p className="mb-1"><strong>Size:</strong> {formatFileSize(viewModal.item?.file_size)}</p>
                  <p className="mb-1"><strong>Type:</strong> {viewModal.item?.mime_type}</p>
                  <p className="mb-1"><strong>Uploaded:</strong> {new Date(viewModal.item?.created_at).toLocaleString()}</p>
                  {/* <p className="mb-0"><strong>URL:</strong> <a href={viewModal.item?.url} target="_blank" rel="noopener noreferrer">{viewModal.item?.url}</a></p> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Media Library</h2>
            <p className="page-subtitle">Manage uploaded images and files</p>
          </div>
          <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
            <i className="bi bi-upload me-2"></i>Upload File
            <input type="file" style={{ display: 'none' }} onChange={handleUpload} accept="image/*,video/*" disabled={uploading} />
          </label>
        </div>
      </div>

      {/* Filters */}
      <div className="stat-card mb-4">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Type Filter</label>
            <select
              className="form-select"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Files</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Search</label>
            <form onSubmit={handleSearch}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by filename..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-primary" type="submit">
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </form>
          </div>
          <div className="col-md-2">
            <label className="form-label">&nbsp;</label>
            <button className="btn btn-outline-primary w-100" onClick={fetchMedia}>
              <i className="bi bi-arrow-clockwise me-2"></i>Refresh
            </button>
          </div>
        </div>
      </div>

      {/*STATS */}
      {stats && (
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="stat-card">
              <small className="text-muted">Total Files</small>
              <h3 className="mb-0">{stats.total_files || 0}</h3>
            </div>
          </div>
          <div className="col-md-4">
            <div className="stat-card">
              <small className="text-muted">Total Size</small>
              <h3 className="mb-0">{formatFileSize(stats.total_size)}</h3>
            </div>
          </div>
          <div className="col-md-4">
            <div className="stat-card">
              <small className="text-muted">Images / Videos</small>
              <h3 className="mb-0">{stats.image_count || 0} / {stats.video_count || 0}</h3>
            </div>
          </div>
        </div>
      )}

      {uploading && (
        <div className="alert alert-info">
          <i className="bi bi-cloud-upload me-2"></i>Uploading file...
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="row g-4">          {media.length === 0 ? (
            <div className="col-12 text-center py-5">
              <i className="bi bi-image fs-1 text-muted d-block mb-3"></i>
              <p className="text-muted">No media files uploaded yet</p>
            </div>
          ) : (
            media.map((item) => (
              <div key={item.id} className="col-md-3">
                <div className="stat-card">
                  {item.file_type === 'image' ? (
                    <img src={item.url} alt={item.original_filename} className="w-100 rounded mb-2" style={{ height: '200px', objectFit: 'cover' }} />
                  ) : (
                    <video src={item.url} className="w-100 rounded mb-2" style={{ height: '200px', objectFit: 'cover' }} />
                  )}
                  <p className="mb-1 small"><strong>{item.original_filename}</strong></p>
                  <p className="text-muted small mb-2">{formatFileSize(item.file_size)}</p>
                  {item.in_use && (
                    <p className="small text-danger mb-2">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      In use in {item.usage_count} place(s)
                    </p>
                  )}
                  <div className="d-flex gap-2">
                    {/* EYE ICON OPENS MODAL */}
                    <button 
                      className="btn btn-sm btn-outline-primary " 
                      onClick={() => setViewModal({ show: true, item })}
                      title="View"
                    >
                      <i className="bi bi-eye"></i>
                    </button>
                    <button 
                      className={`btn btn-sm btn-outline-danger ${item.in_use ? 'disabled' : ''}`} 
                      onClick={() => !item.in_use && setConfirmModal({ show: true, mediaId: item.id })}
                      title={item.in_use ? 'In use - cannot delete' : 'Delete'}
                      disabled={item.in_use}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="mt-4">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;